import { RequestBatcher } from "@/utils/requestBatcher";
import { keepPreviousData, QueryKey, queryOptions, useQueries, useQuery } from "@tanstack/react-query";
import { enumerationsApi } from "../../../modules/enumerations/api";
import { EnumerationType } from "../../../modules/enumerations/enums";
import { AgentEnumerationSearchRequest, Enumerations } from "../../../modules/enumerations/types";
import {
  ENUMERATION_FIELDS,
  EnumerationField,
  PRELOADED_ENUMERATION_TYPES,
  PreloadedEnumerationType,
} from "../../../modules/enumerations/utils";
import { UUID } from "../../../typings/global";
import { queryClient } from "../queryClient";

const ENUMERATIONS_KEY = "enumerations";

export const ENUMERATIONS_QUERY_KEYS = {
  enumerations: (type?: EnumerationType) => (type ? [ENUMERATIONS_KEY, type] : [ENUMERATIONS_KEY]),
  getVehicleBrands: () => ["getVehicleBrands"],
  getVehicleBrand: (id: UUID) => ["getVehicleBrand", id],
  getVehicleModels: (id: UUID) => ["getVehicleModels", id],
  getVehicleModel: (id: UUID) => ["getVehicleModel", id],
  getAgentEnumerations: (ids: UUID[]) => ["getAgentEnumerations", ids],
  searchAgentEnumerations: (payload: AgentEnumerationSearchRequest) => ["searchAgentEnumerations", payload],
};

export const ENUMERATIONS_STALE_TIME = 60 * 60 * 1000;

/** Must be >= the persister's maxAge, or the restored cache is garbage-collected right away. */
export const ENUMERATIONS_GC_TIME = 24 * 60 * 60 * 1000;

export const ENUMERATIONS_RETRY_COUNT = 1;

/** Bump when the persisted shape changes. */
export const ENUMERATIONS_CACHE_VERSION = "enumerations-3";

export const isEnumerationsQuery = (queryKey: QueryKey) => queryKey[0] === ENUMERATIONS_KEY;

/** Other tabs refetch a changed enumeration instead of writing their stale copy back to the shared IndexedDB. */
export const enumerationsChannel = new BroadcastChannel("cliip:enumerations");

export const invalidateEnumeration = (type: EnumerationType) => {
  queryClient.invalidateQueries({ queryKey: ENUMERATIONS_QUERY_KEYS.enumerations(type) });
  enumerationsChannel.postMessage(type);
};

/** Stable reference across renders. */
const EMPTY = Object.freeze([]) as never[];

const enumerationsBatcher = new RequestBatcher<PreloadedEnumerationType, unknown>(async (types) => {
  const { data } = await enumerationsApi.getEnumerations({ types });

  // React Query rejects undefined data
  return types.map((type) => data.enumerations[ENUMERATION_FIELDS[type]] ?? null);
});

export const enumerationQueryOptions = <T extends PreloadedEnumerationType>(type: T) =>
  queryOptions({
    queryKey: ENUMERATIONS_QUERY_KEYS.enumerations(type),
    queryFn: () => enumerationsBatcher.load(type) as Promise<Enumerations[EnumerationField<T>] | null>,
  });

export const useEnumerations = () =>
  useQueries({
    queries: PRELOADED_ENUMERATION_TYPES.map(enumerationQueryOptions),
    combine: (results) => ({
      isPending: results.some((result) => result.isPending),
      isLoadingError: results.some((result) => result.isLoadingError),
    }),
  });

/** Empty while the cache is cold. For branding use `useBranding()`. */
export const useEnumeration = <T extends PreloadedEnumerationType>(type: T) =>
  useQuery(enumerationQueryOptions(type)).data ?? (EMPTY as Enumerations[EnumerationField<T>]);

export const useGetVehicleBrands = () => {
  return useQuery({
    queryKey: ENUMERATIONS_QUERY_KEYS.getVehicleBrands(),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await enumerationsApi.getVehicleBrands();

      return data;
    },
  });
};

export const useGetVehicleBrand = (id: UUID) => {
  return useQuery({
    queryKey: ENUMERATIONS_QUERY_KEYS.getVehicleBrand(id),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await enumerationsApi.getVehicleBrand({ id });

      return data;
    },
  });
};

export const useGetVehicleModels = (id?: UUID) => {
  return useQuery({
    queryKey: ENUMERATIONS_QUERY_KEYS.getVehicleModels(id!),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await enumerationsApi.getVehicleModels({ id: id! });

      return data;
    },
    enabled: !!id,
  });
};

export const useGetVehicleModel = (id: UUID) => {
  return useQuery({
    queryKey: ENUMERATIONS_QUERY_KEYS.getVehicleModel(id),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await enumerationsApi.getVehicleModel({ id });

      return data;
    },
  });
};

export const useGetAgentEnumerations = (ids: UUID[], keepPrevious?: boolean) => {
  return useQuery({
    queryKey: ENUMERATIONS_QUERY_KEYS.getAgentEnumerations(ids),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await enumerationsApi.getAgentEnumerations({ ids });

      return data;
    },
    placeholderData: keepPrevious ? keepPreviousData : undefined,
    enabled: ids.length > 0,
  });
};

export const useSearchAgentEnumerations = (payload: AgentEnumerationSearchRequest) => {
  return useQuery({
    queryKey: ENUMERATIONS_QUERY_KEYS.searchAgentEnumerations(payload),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await enumerationsApi.searchAgentEnumerations(payload);

      return data;
    },
    enabled: payload.keyword.length > 1,
  });
};

/** On demand only - large, and needed just by admin screens. */
export const useGetPermissionDefinitions = () => {
  return useQuery({
    queryKey: ENUMERATIONS_QUERY_KEYS.enumerations(EnumerationType.PERMISSION_DEFINITIONS),
    queryFn: async () => {
      const { data } = await enumerationsApi.getEnumerations({ types: [EnumerationType.PERMISSION_DEFINITIONS] });

      return data.enumerations.permissionDefinitions;
    },
  });
};
